'use client';

import { fetchSlugModel } from '@eliadmin/core/src/api/slugs/fetch-slug-model';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { addProductToFavorites } from '@eliadmin/core/src/api/customers/customers-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { Heart as LuHeart } from 'lucide-react';
import { useState } from 'react';

type AddToFavoritesButtonProps = {
  slug: string;
  className?: string;
  classNameButton?: string;
  isFavorite?: boolean;
  showText?: boolean;
};

export function AddToFavoritesButton(props: AddToFavoritesButtonProps) {
  const customerState = useCustomerStore();
  const [session, setSession] = useState(() => ({
    hasCustomer: customerState.me !== null,
    startedAsGuest: customerState.me === null,
  }));

  // Keep an existing observer mounted through logout/account changes and pending mutations.
  if (customerState.me !== null && !session.hasCustomer) {
    setSession({ ...session, hasCustomer: true });
  }

  return session.hasCustomer || customerState.me !== null ? (
    <CustomerFavoritesButton
      {...props}
      customerState={customerState}
      refetchOnMount={session.startedAsGuest}
    />
  ) : (
    <GuestFavoritesButton {...props} />
  );
}

function GuestFavoritesButton(props: AddToFavoritesButtonProps) {
  const router = useRouter();
  return (
    <FavoritesButtonView
      props={props}
      isFavorite={props.isFavorite ?? undefined}
      isMutating={false}
      onClick={() => router.push(ROUTES.SIGN_IN())}
    />
  );
}

function CustomerFavoritesButton({
  customerState,
  refetchOnMount,
  ...props
}: AddToFavoritesButtonProps & {
  customerState: ReturnType<typeof useCustomerStore.getState>;
  refetchOnMount: boolean;
}) {
  const { me, isInitialized } = customerState;
  const authToken = me?.authToken;
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();
  const queryClient = useQueryClient();
  const productQueryKey = [CacheKey.PRODUCT_DETAIL, locale, props.slug, me?.customer.id ?? 'guest'];

  const { data: product } = useQuery({
    queryKey: productQueryKey,
    queryFn: () =>
      fetchSlugModel({
        model: 'App\\Models\\Product',
        slug: props.slug,
        locale,
        authToken: authToken,
      }),
    enabled: isInitialized && !!authToken && !!props.slug,
    refetchOnWindowFocus: false,
    // A first login must fetch stale cache just like the former disabled→enabled observer.
    refetchOnMount,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: addProductToFavorites,
    onSettled: (data, error) => {
      queryClient.invalidateQueries({
        queryKey: [CacheKey.PRODUCT_DETAIL, locale, props.slug],
      });
      if (!error) {
        queryClient.setQueryData(productQueryKey, {
          ...product,
          data: {
            ...product?.data,
            is_favorite: !isFavorite,
          },
        });
      }
    },
  });

  const isFavorite = product?.data.is_favorite ?? props.isFavorite ?? undefined;

  function handleAddToFavorites() {
    if (!authToken) return router.push(ROUTES.SIGN_IN());
    mutate({ productSlug: props.slug, authToken, locale });
  }

  return (
    <FavoritesButtonView
      props={props}
      isFavorite={isFavorite}
      isMutating={isMutating}
      onClick={handleAddToFavorites}
    />
  );
}

function FavoritesButtonView({
  props,
  isFavorite,
  isMutating,
  onClick,
}: {
  props: AddToFavoritesButtonProps;
  isFavorite: boolean | undefined;
  isMutating: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslations();
  return (
    <Button
      variant="link"
      role="button"
      aria-label={isFavorite ? t('text532') : t('text531')}
      onClick={onClick}
      size={!props.showText ? 'icon-sm' : undefined}
      className={cn(
        'flex items-center justify-center rounded-full bg-background text-accent',
        props.classNameButton,
      )}
    >
      <div className="flex flex-row">
        {isFavorite === undefined || isMutating ? (
          <Spinner className={cn('h-6 w-6', !props.showText && 'h-4 w-4')} />
        ) : (
          <LuHeart
            className={cn(
              'h-6 w-6 cursor-pointer ',
              !props.showText && 'h-4 w-4',
              // isFavorite && 'text-red-500',
              props.className,
            )}
            fill={isFavorite ? 'currentColor' : 'none'}
            role="button"
            strokeWidth={1.5}
          />
        )}
        {props.showText && (
          <span className="link ml-2 ">{isFavorite ? t('text532') : t('text531')}</span>
        )}
      </div>
    </Button>
  );
}
